import { useEffect } from 'react';
import { posts } from '../posts';
import { translations } from '../i18n';
import { useSeo } from './seo';

export const PUBLIC_BASE_URL = 'https://www.schedi.pl';

export type PublicSeo = {
    path: string;
    title: string;
    description: string;
    type?: 'website' | 'article';
    publishedTime?: string;
    changefreq: 'weekly' | 'monthly' | 'yearly';
    priority: string;
    jsonLd: Record<string, unknown> | Record<string, unknown>[];
};

const plLanding = translations.pl.landing;

const webPageSchema = (title: string, description: string, path: string) => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: `${PUBLIC_BASE_URL}${path}`,
    isPartOf: { '@type': 'WebSite', name: 'Schedi', url: PUBLIC_BASE_URL },
});

type SeoPageKey = Exclude<keyof typeof plLanding.seoPages, 'relatedTitle'>;

const seoPageEntries: PublicSeo[] = (Object.keys(plLanding.seoPages) as Array<keyof typeof plLanding.seoPages>)
    .filter((key): key is SeoPageKey => key !== 'relatedTitle')
    .map((key) => {
        const page = plLanding.seoPages[key];
        const faq = plLanding.seoPageFaq[key];
        return {
            path: page.url,
            title: page.metaTitle,
            description: page.metaDescription,
            changefreq: 'monthly',
            priority: key === 'program' || key === 'restaurant' ? '0.9' : '0.8',
            jsonLd: [
                webPageSchema(page.metaTitle, page.metaDescription, page.url),
                {
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        { '@type': 'ListItem', position: 1, name: 'Schedi', item: PUBLIC_BASE_URL },
                        { '@type': 'ListItem', position: 2, name: page.breadcrumb, item: `${PUBLIC_BASE_URL}${page.url}` },
                    ],
                },
                {
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: faq.items.map((item: { q: string; a: string }) => ({
                        '@type': 'Question', name: item.q,
                        acceptedAnswer: { '@type': 'Answer', text: item.a },
                    })),
                },
            ],
        };
    });

const blogEntries: PublicSeo[] = posts.map((post) => ({
    path: `/blog/${post.slug}`,
    title: post.title,
    description: post.excerpt,
    type: 'article',
    publishedTime: post.date,
    changefreq: 'yearly',
    priority: '0.7',
    jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        datePublished: post.date,
        dateModified: post.date,
        author: { '@type': 'Organization', name: 'Schedi', url: PUBLIC_BASE_URL },
        publisher: { '@type': 'Organization', name: 'Schedi', url: PUBLIC_BASE_URL },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${PUBLIC_BASE_URL}/blog/${post.slug}` },
        url: `${PUBLIC_BASE_URL}/blog/${post.slug}`,
        articleSection: post.category,
        timeRequired: `PT${post.readTime}M`,
    },
}));

export const publicSeoEntries: PublicSeo[] = [
    {
        path: '/',
        title: plLanding.seo.title,
        description: plLanding.seo.description,
        changefreq: 'monthly', priority: '1.0',
        jsonLd: [
            { '@context': 'https://schema.org', '@type': 'Organization', name: 'Schedi', url: PUBLIC_BASE_URL, email: 'biuro@schedi.pl' },
            { '@context': 'https://schema.org', '@type': ['WebApplication', 'SoftwareApplication'], name: 'Schedi', applicationCategory: 'BusinessApplication', operatingSystem: 'Web', description: plLanding.seo.description, url: PUBLIC_BASE_URL, offers: { '@type': 'Offer', price: 129, priceCurrency: 'PLN', availability: 'https://schema.org/InStock' } },
            { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: plLanding.faq.items.map((item: { q: string; a: string }) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })) },
        ],
    },
    {
        path: '/blog',
        title: 'Blog - Zarządzanie zespołem zmianowym',
        description: 'Praktyczne poradniki dla managerów gastronomii, retailu i usług. Grafiki pracy, dostępność pracowników, wymiana zmian.',
        changefreq: 'weekly', priority: '0.8',
        jsonLd: webPageSchema('Blog - Zarządzanie zespołem zmianowym', 'Praktyczne poradniki dla managerów gastronomii, retailu i usług.', '/blog'),
    },
    {
        path: '/regulamin', title: 'Regulamin platformy Schedi',
        description: 'Regulamin korzystania z platformy Schedi do zarządzania grafikami pracy.',
        changefreq: 'yearly', priority: '0.4',
        jsonLd: webPageSchema('Regulamin platformy Schedi', 'Regulamin korzystania z platformy Schedi.', '/regulamin'),
    },
    {
        path: '/polityka-prywatnosci', title: 'Polityka prywatności Schedi',
        description: 'Informacje o przetwarzaniu danych osobowych i plikach cookies na platformie Schedi.',
        changefreq: 'yearly', priority: '0.4',
        jsonLd: webPageSchema('Polityka prywatności Schedi', 'Informacje o przetwarzaniu danych osobowych na platformie Schedi.', '/polityka-prywatnosci'),
    },
    ...seoPageEntries,
    ...blogEntries,
];

export const publicSeoByPath = new Map(publicSeoEntries.map((entry) => [entry.path, entry]));
export const publicPaths = publicSeoEntries.map((entry) => entry.path);

export function getPublicSeo(path: string) {
    return publicSeoByPath.get(path);
}

/** Applies the same metadata/schema used by the prerenderer during client navigation. */
export function usePublicSeo(path: string) {
    const seo = getPublicSeo(path);
    useSeo(seo ? { title: seo.title, description: seo.description, url: path, type: seo.type, publishedTime: seo.publishedTime } : { title: 'Schedi', description: '', url: path });

    useEffect(() => {
        if (!seo) return;
        const id = 'public-route-schema';
        let script = document.getElementById(id) as HTMLScriptElement | null;
        if (!script) {
            script = document.createElement('script');
            script.id = id;
            script.type = 'application/ld+json';
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(seo.jsonLd);
        return () => { document.getElementById(id)?.remove(); };
    }, [seo]);
}
